import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { MicIcon, RecordIcon, ChevronIcon, StopRecording } from 'assets/icons';
import { useLoadSensorSelection } from 'hooks/use-load-sensor-selection';
import { SensorSelectionContext, AppContext, appActions } from 'context';
import { classnames, formatRecordingTime } from 'helpers/utils';
import { isOutOfStorageError } from 'helpers/storage';
import { maxTimeClipRecordedInSeconds } from 'config/constants';
import { SensorMenu } from 'common/sensor-menu';
import { useClickOutside } from 'hooks/click-outside';
import { ProgressBar } from 'common/progress-bar';
import { useSelfRecording } from 'hooks/use-self-recording';
import startRecordingSound from 'assets/audios/start-recording-sound.mp3';
import endRecordingSound from 'assets/audios/end-recording-sound.mp3';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { Toggle } from 'common/toggle';
import { NotificationObject } from 'models/notificationObject';
import {
  AlbumContentType,
  NotificationType,
  SelfRecordingStatus,
} from 'common/enums';
import Bowser from 'bowser';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { VideosRecordedType } from 'context/self-recording-context/context-reducer';
import { ContentIcons } from 'assets/icons/content-icons';
import { DocumentType } from 'networking/types/documents';
import { LocalStorageApi } from 'helpers/local-storage';
import styles from './audio-recorder.module.scss';

type AudioRecorderProps = {
  className?: string;
  onStopRecording: (docResponse?: DocumentType) => void;
  showToggle?: boolean;
  title?: string;
  // The save was refused because the vault is out of storage.
  onOutOfStorage?: () => void;
};

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  className = '',
  onStopRecording,
  showToggle = true,
  title,
  onOutOfStorage,
}) => {
  const { sensorSelection } = useContext(SensorSelectionContext);
  useLoadSensorSelection({ loadCamera: false, loadMicrophone: true });
  const {
    mediaRecorderState,
    countingTime,
    closeAllTracks,
    createMediaRecorder,
  } = useSelfRecording(true);
  const { state: selfRecordingState, dispatch } =
    useContext(SelfRecordingContext);
  const { state: appState, dispatch: generalDispatch } = useContext(AppContext);

  const [timer, setTimer] = useState(0);
  const [microphoneMenuOpen, setMicrophoneMenuOpen] = useState(false);
  const [continueInNewClip, setContinueInNewClip] = useState(showToggle);
  const [showToggleMessage, setShowToggleMessage] = useState(false);

  const microphoneMenuRef = useRef<HTMLDivElement>(null);
  const endRecordingSoundRef = useRef<HTMLAudioElement>(null);
  const startRecordingSoundRef = useRef<HTMLAudioElement>(null);
  const isUploading = useRef(false);
  const positionInBatchRef = useRef<number | undefined>(undefined);

  const { browser } = Bowser.parse(window.navigator.userAgent);
  const continueInNewClipPatchForSafari = browser.name === 'Safari';

  useClickOutside(microphoneMenuRef, () => setMicrophoneMenuOpen(false));

  const canStartRecording = () =>
    sensorSelection.microphone.enabled &&
    !sensorSelection.microphone.error &&
    !selfRecordingState.isRecording;

  const getInformationById = (requestId: string) =>
    selfRecordingState.videosRecorded.find(
      (video) => video.requestId === requestId,
    );

  const createClip = async (requestId: string) => {
    const clipToSave = getInformationById(requestId);
    if (!clipToSave) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
      return undefined;
    }
    const clipTitle = title?.trim();

    const createClipRequest = {
      clip: {
        videoSignedId: clipToSave.videoSignedId,
        name: clipTitle || clipToSave.name,
        recordedAt: new Date(Date.now() + parseInt(requestId, 10) * 1000),
        batchId: selfRecordingState.batchId,
        positionInBatch:
          clipToSave.positionInBatchToClip ?? positionInBatchRef.current,
        albumId: selfRecordingState.albumId,
      },
    };

    try {
      const guestToken = LocalStorageApi.get('guestToken');
      if (guestToken && !selfRecordingState.albumId) {
        generalDispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Something went wrong',
            message: 'No topic selected. Please select a topic and try again.',
            type: NotificationType.Error,
          }),
        });
        return undefined;
      }
      const response = guestToken
        ? await VaultsController.createDocumentWithToken(
            selfRecordingState.albumId!,
            selfRecordingState.vaultId,
            createClipRequest,
          )
        : await VaultsController.createDocument(
            selfRecordingState.vaultId,
            createClipRequest,
          );

      if (response) {
        const responseWithPreview: DocumentType = {
          ...response,
          fileUrl: response.fileUrl || clipToSave.videoUrl,
          contentType: response.contentType || AlbumContentType.audio,
        };
        dispatch({
          type: selfRecordingActions.setUploadResponse,
          uploadResponse: {
            uploadId: requestId,
            response: { ...responseWithPreview, user: appState.data.user },
          },
        });
      }
      return response;
    } catch (error) {
      // Over the vault's storage: the host shows its out-of-storage dialog.
      if (onOutOfStorage && isOutOfStorageError(error)) {
        onOutOfStorage();
        return undefined;
      }
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
      return undefined;
    }
  };

  const addPositionInBatch = (video: VideosRecordedType) => {
    if (positionInBatchRef.current) {
      positionInBatchRef.current += 1;
      dispatch({
        type: selfRecordingActions.setPositionInBatchToClip,
        positionInBatchToClip: {
          positionInBatch: positionInBatchRef.current,
          requestId: video.requestId,
        },
      });
    }
  };

  const handleSaveAllClips = async () => {
    selfRecordingState.videosRecorded.forEach(async (video) => {
      dispatch({
        type: selfRecordingActions.addUpload,
        addUpload: {
          type: AlbumContentType.audio,
          data: 'Audio Recording',
          isUploading: true,
          uploadId: video.requestId,
        },
      });
      addPositionInBatch(video);
      const docResponse = await createClip(video.requestId);
      dispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: {
          isUploading: false,
          error: !docResponse,
          uploadId: video.requestId,
          type: AlbumContentType.audio,
          data: 'Audio Recording',
        },
      });
      if (docResponse) {
        onStopRecording?.(docResponse);
        dispatch({
          type: selfRecordingActions.deleteVideo,
          videoToDelete: video.requestId,
        });
      }
    });
  };

  const stopRecording = () => {
    endRecordingSoundRef.current?.play();
    mediaRecorderState?.stop();
    dispatch({ type: selfRecordingActions.setClipNumber, clipNumber: 0 });
    isUploading.current = true;
  };

  let myInterval: NodeJS.Timeout;

  useEffect(() => {
    if (countingTime) {
      myInterval = setInterval(() => {
        setTimer((t) => t + 1);
      }, 1000);
    }
    return () => {
      setTimer(0);
      clearInterval(myInterval);
    };
  }, [countingTime]);

  useEffect(() => {
    const handleTimer = () => {
      if (timer === maxTimeClipRecordedInSeconds - 30 && !continueInNewClip) {
        setShowToggleMessage(true);
      }
      if (timer === maxTimeClipRecordedInSeconds) {
        if (mediaRecorderState && continueInNewClip && canStartRecording()) {
          const stopPromise = new Promise<void>((resolve) => {
            const originalOnStop = mediaRecorderState.onstop;
            mediaRecorderState!.onstop = (ev) => {
              originalOnStop?.call(mediaRecorderState, ev);
              resolve();
            };
          });
          mediaRecorderState?.stop();
          stopPromise.then(() => {
            setTimeout(() => {
              mediaRecorderState?.start();
              dispatch({
                type: selfRecordingActions.setClipNumber,
                clipNumber: selfRecordingState.clipNumber + 1,
              });
            }, 100);
          });
        } else {
          stopRecording();
        }
      }
    };
    handleTimer();
  }, [timer, continueInNewClip]);

  useEffect(() => {
    if (isUploading.current && selfRecordingState.readyForNextStep) {
      isUploading.current = false;
      closeAllTracks();
      handleSaveAllClips();
    }
  }, [selfRecordingState]);

  useEffect(() => {
    const processing = selfRecordingState.videosRecorded.some(
      (video) => video.status === SelfRecordingStatus.processing,
    );
    const haveErrors = selfRecordingState.videosRecorded.some(
      (video) => video.status === SelfRecordingStatus.error,
    );
    if (!processing && haveErrors) {
      isUploading.current = false;
    }
  }, [selfRecordingState]);

  const startRecording = async () => {
    startRecordingSoundRef.current?.play();
    const mediaRecorder = await createMediaRecorder();
    if (mediaRecorder) {
      mediaRecorder.start();
      dispatch({ type: selfRecordingActions.setClipNumber, clipNumber: 1 });
    }
  };

  const getMicrophoneLabel = () =>
    sensorSelection.microphone.selectedOption?.label || 'Select microphone';

  return (
    <div className={classnames(styles.audioRecorder, className)}>
      <div className={styles.recordingIcon}>{ContentIcons.audio}</div>
      <div className={styles.controls}>
        <div className={styles.timeAndControls}>
          <div className={styles.timeDisplay}>
            <span className={styles.currentTime}>
              {formatRecordingTime(timer)}
            </span>
            <div className={styles.progressBar}>
              <ProgressBar
                percentageUsed={(timer / maxTimeClipRecordedInSeconds) * 100}
                reachingLimit={false}
                height={8}
              />
            </div>
            <span className={styles.maxTime}>
              {formatRecordingTime(maxTimeClipRecordedInSeconds)}
            </span>
          </div>

          <Button
            buttonSize={ButtonSize.Small}
            buttonStyle={
              selfRecordingState.isRecording
                ? ButtonStyle.DestructivePrimary
                : ButtonStyle.Primary
            }
            onClick={
              selfRecordingState.isRecording ? stopRecording : startRecording
            }
            disabled={!selfRecordingState.isRecording && !canStartRecording()}
          >
            <div className={styles.recordButton}>
              {selfRecordingState.isRecording ? (
                <>
                  <StopRecording />
                  Stop Recording
                </>
              ) : (
                <>
                  <RecordIcon />
                  Start Recording
                </>
              )}
            </div>
          </Button>
        </div>

        <div className={styles.downSection}>
          <div className={styles.microphoneSelection}>
            <div ref={microphoneMenuRef} className={styles.microphoneDropdown}>
              <button
                type="button"
                className={styles.microphoneButton}
                onClick={() => setMicrophoneMenuOpen(!microphoneMenuOpen)}
                disabled={selfRecordingState.isRecording}
              >
                <MicIcon />
                <span className={styles.microphoneLabel}>
                  {getMicrophoneLabel()}
                </span>
                <ChevronIcon
                  className={`${styles.chevron} ${microphoneMenuOpen ? styles.chevronOpen : ''}`}
                />
              </button>
              <SensorMenu
                containerRef={microphoneMenuRef}
                sensorType="microphone"
                menuOpen={microphoneMenuOpen}
                setMenuOpen={setMicrophoneMenuOpen}
              />
            </div>
          </div>
          {showToggle && (
            <div className={styles.toggleAndText}>
              <Toggle
                id="audio-continue-switch"
                tooltipText={
                  continueInNewClipPatchForSafari
                    ? 'Continuing a recording is not currently available for Safari browsers due to performance issues. We will be releasing an update in the near future.'
                    : 'Your time is running out. Click on this toggle to continue recording in a new clip.'
                }
                checked={!continueInNewClipPatchForSafari && continueInNewClip}
                disabled={continueInNewClipPatchForSafari}
                onChangeFn={() => {
                  setContinueInNewClip(
                    !continueInNewClipPatchForSafari && !continueInNewClip,
                  );
                }}
                showTooltip={showToggleMessage}
                containerClass={styles.toggleContainer}
              />
              <span className="text__body__regular__small__gray700">
                Continue in new clip if time runs out
              </span>
            </div>
          )}
        </div>
      </div>

      <audio ref={startRecordingSoundRef} hidden>
        <source src={startRecordingSound} type="audio/mp3" />
        <track kind="captions" />
      </audio>
      <audio ref={endRecordingSoundRef} hidden>
        <source src={endRecordingSound} type="audio/mp3" />
        <track kind="captions" />
      </audio>
    </div>
  );
};

export { AudioRecorder };
